<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { NatcaShell, NatcaAppFooter, useNatcaTheme, type NatcaUser, type NatcaBreadcrumb } from '@natca-itc/ui-shell'
import { useSession } from '@/stores/session'
import { signOut } from '@/lib/auth'
import { navFor } from '@/lib/nav'

const route = useRoute()
const router = useRouter()
const session = useSession()

const { setTheme } = useNatcaTheme()
onMounted(() => {
  let saved: string | null = null
  try { saved = localStorage.getItem('natca-theme') } catch { /* private mode */ }
  setTheme(saved ?? 'dark')
})

const nav = computed(() => navFor(session.hasRole))

const user = computed<NatcaUser | undefined>(() => {
  const id = session.identity
  if (!id) return undefined
  const name = id.name ?? id.memberNumber ?? 'Signed in'
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('')
  return { name, initials, email: id.email ?? undefined, memberNumber: id.memberNumber ?? undefined }
})

const breadcrumbs = computed<NatcaBreadcrumb[]>(() =>
  route.name === 'home' ? [] : [{ label: 'Convention', to: '/' }, { label: route.meta.title ?? '' }])

async function onProfileAction(action: string) {
  if (action === 'signout' || action === 'logout' || action === 'sign-out') {
    session.clear()
    await signOut()
    await router.push({ name: 'sign-in' })
  }
}

const buildLabel = `Convention · ${import.meta.env.MODE}`
</script>

<template>
  <router-view v-if="route.meta.bare" />
  <NatcaShell
    v-else
    app-id="convention"
    app-name="Convention"
    :tabs="nav.tabs"
    :user="user"
    :sidebar-sections="nav.sections"
    :breadcrumbs="breadcrumbs"
    :show-search="false"
    :show-notifications="false"
    @profile-action="onProfileAction"
  >
    <router-view />
    <template #footer>
      <NatcaAppFooter>{{ buildLabel }}</NatcaAppFooter>
    </template>
  </NatcaShell>
</template>
